/**
 * Stats dashboard data, sourced from the Owners Hub Stats API.
 *
 * Wires the Brand-metrics cards (New Players, Financial Performance, Money Flow,
 * Earnings) and the Operational Performance tiles to `state.ownerHubStats`,
 * returning the same `Stats*` view shapes the presentational sections already
 * consume (see `mock-data/types.ts`) — so the sections are untouched.
 *
 * The range → window → fetch machinery is the SAME as the Home dashboard: one
 * `daily_client_activity` history request per range, plus a period-over-period
 * baseline for the deltas (the live `TODAY` range makes a separate prior
 * call; the longer ranges split one extended call). See `useHomeStats.ts` and
 * `stats-shape.ts` for the full rationale; this hook only differs in WHICH KPIs
 * it derives from the buckets.
 *
 * Field math (per the metrics spec):
 *  - Gross Revenue / Stakes / Wins / Deposits / Withdrawals: casino_* + sport_*
 *    sums (same as Home), scaled integers → $ via {@link AMOUNT_SCALE}.
 *  - Net Revenue   = (sport_ggr_real + casino_ggr_real) − bonus_amount_transformed
 *  - Bonus cost    = bonus_amount_transformed
 *  - Bonus/GGR     = (bonus_amount_transformed / Net Revenue) × 100  (a %, unscaled)
 *
 * Operational Performance:
 *  - Active Players       = `active_players`, which the backend rolls up as a MAX
 *    (a peak count, not a sum) — so it is aggregated with {@link maxBuckets}, not
 *    summed across the window.
 *  - Player Average Wager = total stakes (casino + sport) / Active Players. Long
 *    blocked on its divisor; now that `active_players` lands it is computed here.
 *  - Live RTP             = total wins (casino + sport) / total stakes × 100, i.e. the
 *    share of wagered money returned to players. The scale cancels in the ratio, so it
 *    runs on the raw scaled integers (same as Bonus/GGR); `–` when stakes are zero.
 *
 * Placeholder markers (a figure with no real value, delta suppressed): `?` = not wired
 * to an API field (Player Balance), `N/A` = the request is failing, `–` = real data but
 * the value is undefined this period (a ratio dividing by zero, e.g. RTP with no stakes).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchStatsDataSetHistory,
  selectStatsDataSetHistory,
  selectStatsDataSetHistoryError,
  selectStatsDataSetHistoryLoading,
  type StatsHistoryEntry,
  type StatsHistoryRef,
} from '@/store/slices/ownerHubStats';

import type {
  HomePeriod,
  StatsDelta,
  StatsFigure,
  StatsLead,
  StatsStat,
  StatsNewPlayers,
  StatsFinancial,
  StatsMoneyFlow,
  StatsEarnings,
  StatsKpi,
} from '../../mock-data/types';
import { AMOUNT_SCALE, cohortLabels, customDays, homeWindow, intervalIncludesToday, shiftWindow } from '../home/stats-mapping';
import { chartSeries, dayPair, deltaPct, liveBuckets, matchedBaseline, maxBuckets, resolvePeriods, sumBuckets, toBuckets } from '../home/stats-shape';
import { LIVE_REFRESH_MS, useRefreshTick } from '../home/useRefreshTick';

export type StatsMetricsResult = {
  newPlayers: StatsNewPlayers;
  financial: StatsFinancial;
  moneyFlow: StatsMoneyFlow;
  earnings: StatsEarnings;
  operational: StatsKpi[];
  /** True only while there's no cached history to show (stale-while-revalidate). */
  loading: boolean;
};

/** The single data set every Brand-metric reads (no dimensions → flat metrics). */
const DATA_SET = 'daily_client_activity';

/** Micro-units → major currency unit (same factor Home uses). */
const MONEY = 1 / AMOUNT_SCALE;

/** Bucket fields per KPI. Money totals sum casino_* + sport_*; `_real` GGR and the
 *  transformed bonus back the Earnings card. `registrations` / `ftds` are counts. */
const FIELDS = {
  registrations: ['registrations'],
  ftds: ['ftds'],
  ggr: ['casino_ggr', 'sport_ggr'],
  stakes: ['casino_bet_amount', 'sport_bet_amount'],
  wins: ['casino_win_amount', 'sport_win_amount'],
  deposits: ['deposit_amount'],
  withdrawals: ['withdraw_processed_amount'],
  ggrReal: ['sport_ggr_real', 'casino_ggr_real'],
  bonus: ['bonus_amount_transformed'],
  /** Peak active players — the backend rolls this up as a MAX (see `maxBuckets`). */
  activePlayers: ['active_players'],
} as const;

/** Period-over-period change as a pre-formatted Stats delta pill. Uses the same
 *  base logic as Home (`deltaPct`: prior equal period, ±100% from a zero baseline);
 *  the 2-decimal `{ label, dir }` shape is the Stats page's own presentation. */
const statsDelta = (current: number, baseline: number, invertTone = false): StatsDelta => {
  const pct = deltaPct(current, baseline);
  const dir: StatsDelta['dir'] = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
  const delta: StatsDelta = { label: `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`, dir };
  // Inverted metrics (e.g. Live RTP, where a higher % is worse for the operator) keep
  // the real arrow + sign but flip the colour: a rise reads red, a fall green.
  if (invertTone && dir !== 'flat') delta.tone = dir === 'up' ? 'negative' : 'positive';
  return delta;
};

type Fmt = { prefix?: string; suffix?: string; decimals?: number; scale?: number };

/** Shape a raw aggregate into a display figure (apply scale + affixes; flag errors). */
const figure = (raw: number, fmt: Fmt, errored: boolean): StatsFigure => ({
  value: raw * (fmt.scale ?? 1),
  prefix: fmt.prefix,
  suffix: fmt.suffix,
  decimals: fmt.decimals,
  error: errored || undefined,
});

/** A headline figure (label + delta vs the prior period + "vs …" caption). */
const lead = (label: string, rawCurrent: number, rawBaseline: number, fmt: Fmt, vs: string, errored: boolean): StatsLead => ({
  label,
  ...figure(rawCurrent, fmt, errored),
  delta: statsDelta(rawCurrent, rawBaseline),
  vs,
});

/** A bare secondary figure (no delta). */
const stat = (label: string, raw: number, fmt: Fmt, errored: boolean): StatsStat => ({ label, ...figure(raw, fmt, errored) });

/**
 * Stale-while-revalidate loading: the skeleton shows ONLY when there is no cached
 * history. Mirrors `useHomeStats` — first load (no entry) shows the skeleton; a
 * background refresh over an existing entry keeps the figures on screen; a first
 * load that fails stops loading (falls through to the `N/A` error state); a missing
 * brand reads as loading.
 */
const sectionLoading = (
  hasBrand: boolean,
  loadingFlag: boolean,
  entry: StatsHistoryEntry | undefined,
  error: string | undefined,
): boolean => !hasBrand || (entry === undefined && (loadingFlag || error === undefined));

/**
 * @param casinoInstanceId Active brand id (`displayBrand?.id`); `undefined` keeps
 *        the page in its loading state until a brand resolves.
 * @param interval The selected range — the SAME `HomePeriod` set the Home Key
 *        Metrics header uses (`TODAY` / `YESTERDAY` / `7D` / `30D` / `90D` / `6M` / `1Y`, or a custom run of days).
 */
export const useStatsMetrics = (casinoInstanceId: string | undefined, interval: HomePeriod): StatsMetricsResult => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // `TODAY` drives the window STRATEGY (separate prior call); but the live latest-day
  // slice re-aggregates ~every 5 min, so every range whose window includes today (all
  // but Yesterday) re-polls on that cadence. The tick also re-reads the clock so the
  // window rolls over at UTC midnight.
  const live = interval === 'TODAY';
  const includesToday = intervalIncludesToday(interval);
  const tick = useRefreshTick(includesToday, LIVE_REFRESH_MS);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const window = React.useMemo(() => homeWindow(interval, Date.now()), [interval, tick]);
  const priorWindow = React.useMemo(() => shiftWindow(window, window.spanDays), [window]);

  // Today fetches just the current window (a SEPARATE prior call keeps it inside
  // the backend's fresh window); every other range fetches ONE extended window
  // that also covers the prior period (split client-side).
  const mainWindow = live
    ? { startDate: window.startDate, endDate: window.endDate, intervalSlice: window.intervalSlice }
    : { startDate: priorWindow.startDate, endDate: window.endDate, intervalSlice: window.intervalSlice };

  const mainRef: StatsHistoryRef | undefined = casinoInstanceId ? { casinoInstanceId, dataSet: DATA_SET, ...mainWindow } : undefined;
  const priorRef: StatsHistoryRef | undefined =
    casinoInstanceId && live
      ? { casinoInstanceId, dataSet: DATA_SET, startDate: priorWindow.startDate, endDate: priorWindow.endDate, intervalSlice: window.intervalSlice }
      : undefined;

  const mainEntry = useAppSelector((s) => selectStatsDataSetHistory(s, mainRef));
  const mainLoading = useAppSelector((s) => selectStatsDataSetHistoryLoading(s, mainRef));
  const mainError = useAppSelector((s) => selectStatsDataSetHistoryError(s, mainRef));
  const priorEntry = useAppSelector((s) => selectStatsDataSetHistory(s, priorRef));

  // Main call — re-dispatched on the tick so Today refreshes in place.
  React.useEffect(() => {
    if (!casinoInstanceId) return;
    dispatch(
      fetchStatsDataSetHistory({
        casinoInstanceId,
        dataSet: DATA_SET,
        startDate: mainWindow.startDate,
        endDate: mainWindow.endDate,
        intervalSlice: mainWindow.intervalSlice,
      }),
    );
  }, [dispatch, casinoInstanceId, mainWindow.startDate, mainWindow.endDate, mainWindow.intervalSlice, tick]);

  // Separate prior call — Today only (other ranges get the prior period from the
  // extended main call above).
  React.useEffect(() => {
    if (!casinoInstanceId || !live) return;
    dispatch(
      fetchStatsDataSetHistory({
        casinoInstanceId,
        dataSet: DATA_SET,
        startDate: priorWindow.startDate,
        endDate: priorWindow.endDate,
        intervalSlice: window.intervalSlice,
      }),
    );
  }, [dispatch, casinoInstanceId, live, priorWindow.startDate, priorWindow.endDate, window.intervalSlice]);

  const hasBrand = Boolean(casinoInstanceId);
  // Drop future buckets the live range may report, then resolve the current +
  // prior periods (separate call for Today, split otherwise).
  const mainBuckets = liveBuckets(mainEntry ? toBuckets(mainEntry.data) : [], Date.now());
  const priorBuckets = priorEntry ? toBuckets(priorEntry.data) : [];
  const { current, previous } = resolvePeriods(mainBuckets, priorBuckets, live, Date.parse(window.startDate));
  const baseline = matchedBaseline(current, previous);

  // Keep BOTH the sparse axis labels and the full per-point labels: for the live
  // hourly ranges (Today/Yesterday) xLabels is sampled (~every 4th bucket) while
  // pointLabels has one entry per sample, so the tooltip heading lands on the
  // exact hour instead of the nearest sparse axis tick (mirrors the Home dashboard).
  const { xLabels: labels, pointLabels } = cohortLabels(interval, current.map((b) => b.date));
  const loading = sectionLoading(hasBrand, mainLoading, mainEntry, mainError);
  // Hard error: the request failed and nothing is cached (so not loading): render `N/A`.
  const errored = !loading && mainError !== undefined && mainEntry === undefined;
  // The "vs …" caption per range (the foreign `COMPARISON_LABEL` map is English and
  // also the i18n key); localize it here at the render site so it re-evaluates on a
  // language switch. Each branch wraps an explicit literal so every string is harvested.
  const vs = ((): string => {
    switch (interval) {
      case 'TODAY':
        return t('vs yesterday');
      case 'YESTERDAY':
        return t('vs previous day');
      case '7D':
        return t('vs previous 7 days');
      case '30D':
        return t('vs previous 30 days');
      case '90D':
        return t('vs previous 90 days');
      case '6M':
        return t('vs previous 6 months');
      case '1Y':
        return t('vs previous year');
      default:
        // A custom range compares with the same number of days just before it.
        return t('vs the {{n}} days before', { n: customDays(interval)?.days ?? 7 });
    }
  })();

  // Earnings is computed (sum/subtract/ratio over raw sums); the scale cancels in
  // the ratio so it works on raw scaled integers. netRevenue = real GGR − bonus.
  const netRevenueRaw = sumBuckets(current, FIELDS.ggrReal) - sumBuckets(current, FIELDS.bonus);
  const netRevenueBaseRaw = sumBuckets(baseline, FIELDS.ggrReal) - sumBuckets(baseline, FIELDS.bonus);
  const bonusRaw = sumBuckets(current, FIELDS.bonus);
  const bonusGgrRatio = netRevenueRaw !== 0 ? (bonusRaw / netRevenueRaw) * 100 : 0;

  const newPlayers: StatsNewPlayers = {
    registration: lead(t('Registrations'), sumBuckets(current, FIELDS.registrations), sumBuckets(baseline, FIELDS.registrations), {}, vs, errored),
    ftds: lead(t('FTDs'), sumBuckets(current, FIELDS.ftds), sumBuckets(baseline, FIELDS.ftds), {}, vs, errored),
    chart: {
      labels,
      pointLabels,
      registrations: chartSeries(current, FIELDS.registrations, 1),
      ftds: chartSeries(current, FIELDS.ftds, 1),
    },
    days: {
      registrations: dayPair(current, previous, FIELDS.registrations, 1),
      ftds: dayPair(current, previous, FIELDS.ftds, 1),
    },
  };

  const financial: StatsFinancial = {
    grossRevenue: lead(t('Gross Revenue'), sumBuckets(current, FIELDS.ggr), sumBuckets(baseline, FIELDS.ggr), { prefix: '$', scale: MONEY }, vs, errored),
    stakes: stat(t('Stakes'), sumBuckets(current, FIELDS.stakes), { prefix: '$', scale: MONEY }, errored),
    wins: stat(t('Wins'), sumBuckets(current, FIELDS.wins), { prefix: '$', scale: MONEY }, errored),
    chart: {
      labels,
      pointLabels,
      ggr: chartSeries(current, FIELDS.ggr, MONEY),
      stakes: chartSeries(current, FIELDS.stakes, MONEY),
      wins: chartSeries(current, FIELDS.wins, MONEY),
    },
    days: {
      ggr: dayPair(current, previous, FIELDS.ggr, MONEY),
      stakes: dayPair(current, previous, FIELDS.stakes, MONEY),
      wins: dayPair(current, previous, FIELDS.wins, MONEY),
    },
  };

  const moneyFlow: StatsMoneyFlow = {
    deposits: lead(t('Deposits'), sumBuckets(current, FIELDS.deposits), sumBuckets(baseline, FIELDS.deposits), { prefix: '$', scale: MONEY }, vs, errored),
    withdrawals: stat(t('Withdrawals'), sumBuckets(current, FIELDS.withdrawals), { prefix: '$', scale: MONEY }, errored),
    // No balance metric in the data set — render `?` (flips to live when one lands).
    playerBalance: { label: t('Player Balance'), value: 0, prefix: '$', missing: true },
  };

  const earnings: StatsEarnings = {
    netRevenue: lead(t('Net Revenue'), netRevenueRaw, netRevenueBaseRaw, { prefix: '$', scale: MONEY }, vs, errored),
    bonusCost: stat(t('Bonus cost'), bonusRaw, { prefix: '$', scale: MONEY }, errored),
    // Zero net revenue leaves the ratio undefined (a divide by zero): render `–`
    // (indeterminate). Gated on `!errored` so a hard error still shows `N/A`.
    bonusGgrRatio: { label: t('Bonus/GGR Ratio'), value: bonusGgrRatio, suffix: '%', decimals: 2, indeterminate: (!errored && netRevenueRaw === 0) || undefined, error: errored || undefined },
  };

  // Operational Performance. Active Players is MAX-aggregated (peak across the window,
  // never summed); Player Average Wager = total stakes / Active Players (in major
  // units). Live RTP = total wins / total stakes × 100 (the scale cancels in the ratio,
  // so it runs on the raw scaled integers). All compare to the prior period; guard each
  // divide against a zero denominator.
  const activePlayers = maxBuckets(current, FIELDS.activePlayers);
  const activePlayersBase = maxBuckets(baseline, FIELDS.activePlayers);
  const avgWager = activePlayers > 0 ? (sumBuckets(current, FIELDS.stakes) * MONEY) / activePlayers : 0;
  const avgWagerBase = activePlayersBase > 0 ? (sumBuckets(baseline, FIELDS.stakes) * MONEY) / activePlayersBase : 0;
  const stakesRaw = sumBuckets(current, FIELDS.stakes);
  const stakesBaseRaw = sumBuckets(baseline, FIELDS.stakes);
  const liveRtp = stakesRaw !== 0 ? (sumBuckets(current, FIELDS.wins) / stakesRaw) * 100 : 0;
  const liveRtpBase = stakesBaseRaw !== 0 ? (sumBuckets(baseline, FIELDS.wins) / stakesBaseRaw) * 100 : 0;
  const operational: StatsKpi[] = [
    { id: 'active-players', label: t('Active Players'), value: activePlayers, delta: statsDelta(activePlayers, activePlayersBase), vs, error: errored || undefined },
    // No active players leaves the average wager undefined (a divide by zero): render
    // `–` (indeterminate) rather than a misleading $0.00. Gated on `!errored` so a hard
    // error still shows `N/A`.
    { id: 'avg-wager', label: t('Player Average Wager'), value: avgWager, prefix: '$', decimals: 2, delta: statsDelta(avgWager, avgWagerBase), vs, indeterminate: (!errored && activePlayers === 0) || undefined, error: errored || undefined },
    // No wagering in the period leaves RTP undefined (0 / 0): render `–` (indeterminate)
    // rather than a misleading 0.00%. On a hard error the `N/A` state wins, so gate the
    // indeterminate flag on `!errored`.
    {
      id: 'live-rtp',
      label: t('Live RTP'),
      value: liveRtp,
      suffix: '%',
      decimals: 2,
      // RTP is an INVERTED metric: a higher % returned to players is worse for the
      // operator, so flip the delta colour (a rise reads red, a fall green) while keeping
      // the real arrow + sign.
      delta: statsDelta(liveRtp, liveRtpBase, true),
      vs,
      indeterminate: (!errored && stakesRaw === 0) || undefined,
      error: errored || undefined,
    },
  ];

  return { newPlayers, financial, moneyFlow, earnings, operational, loading };
};

export default useStatsMetrics;
